import { Clip, clipTimeSec, state } from './state.ts';
import { getScrubFrame } from './frameCache.ts';

/**
 * All layout geometry + drawing lives here as one pure path: the preview rAF
 * loop and the MP4 exporter both call composite(), so the preview is exactly
 * what exports (divider/label chrome is preview-only).
 */

export interface Dims {
  w: number;
  h: number;
}

const PREVIEW_MAX = 1920;
const FALLBACK: Dims = { w: 640, h: 360 };

function activeClips(): Clip[] {
  return state.clips.filter((c) => !c.error);
}

/** Natural output size for the current layout, derived from the dominant clip. */
export function layoutDims(): Dims {
  const clips = activeClips();
  if (!clips.length) return FALLBACK;
  const w0 = clips[0].width || FALLBACK.w;
  const h0 = clips[0].height || FALLBACK.h;
  switch (state.layout) {
    case 'side-by-side':
      return { w: w0 * 2, h: h0 };
    case 'top-bottom':
      return { w: w0, h: h0 * 2 };
    case 'grid':
      return { w: w0 * 2, h: h0 * 2 };
    case 'dominant-right':
      return { w: Math.round(w0 * 1.5), h: h0 };
    case 'dominant-bottom':
      return { w: w0, h: Math.round(h0 * 1.5) };
    default:
      return { w: w0, h: h0 };
  }
}

function scaleDims(d: Dims, maxW: number, maxH: number): Dims {
  const s = Math.min(1, maxW / d.w, maxH / d.h);
  return { w: Math.round(d.w * s), h: Math.round(d.h * s) };
}

/** Internal preview-canvas resolution (capped for smooth drawing). */
export function previewDims(): Dims {
  return scaleDims(layoutDims(), PREVIEW_MAX, PREVIEW_MAX);
}

const RES_CAP: Record<string, number> = { '2160': 2160, '1440': 1440, '1080': 1080, '720': 720 };

/** Export resolution: natural layout size capped by the resolution preset. */
export function exportDims(): Dims {
  const d = layoutDims();
  const cap = RES_CAP[state.exportRes];
  if (!cap) return d;
  const s = Math.min(1, cap / Math.min(d.w, d.h));
  return { w: Math.round(d.w * s), h: Math.round(d.h * s) };
}

// ---------- drawing ----------

function drawClip(
  ctx: CanvasRenderingContext2D,
  clip: Clip,
  x: number,
  y: number,
  w: number,
  h: number,
  preview = false
): void {
  ctx.fillStyle = '#000';
  ctx.fillRect(x, y, w, h);
  if (!clip.width || !clip.height) return;

  // While paused in the preview, prefer the WebCodecs scrub cache — the exact
  // decoded frame, available instantly on long-GOP sources. Export and
  // playback stay on the <video> path.
  let source: CanvasImageSource | null = null;
  if (preview && !state.playing && clip.video) {
    source = getScrubFrame(clip, clipTimeSec(clip, state.frame));
  }
  if (!source) {
    const el = clip.video ?? clip.image;
    if (!el) return;
    // Keep drawing while a seek is in flight — readyState dips below
    // HAVE_CURRENT_DATA mid-seek and skipping would flash black on every
    // scrub; drawImage paints the last decoded frame (no-op if none).
    if (clip.video && clip.video.readyState < 2 && !clip.video.seeking) return;
    source = el;
  }

  const scale = Math.min(w / clip.width, h / clip.height);
  const dw = clip.width * scale;
  const dh = clip.height * scale;
  ctx.globalAlpha = clip.opacity;
  ctx.drawImage(source, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  ctx.globalAlpha = 1;
}

function drawLabel(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  align: 'left' | 'right' = 'left'
): void {
  const size = Math.max(11, Math.round(ctx.canvas.width * 0.011));
  ctx.font = `${size}px system-ui, sans-serif`;
  const pad = Math.round(size * 0.5);
  const width = ctx.measureText(text).width + pad * 2;
  const bx = align === 'left' ? x : x - width;
  ctx.fillStyle = 'rgba(0,0,0,0.55)';
  ctx.fillRect(bx, y, width, size + pad);
  ctx.fillStyle = 'rgba(255,255,255,0.9)';
  ctx.fillText(text, bx + pad, y + size + pad * 0.25 - 2);
}

function label(clip: Clip, index: number): string {
  const name = clip.name.length > 28 ? `${clip.name.slice(0, 26)}…` : clip.name;
  return `${index + 1} · ${name}`;
}

/**
 * Draw the current state of every clip's media element into ctx at w×h.
 * Callers are responsible for having the videos seeked/playing at the right
 * time — this only draws. `preview` adds labels + the wipe divider handle.
 */
export function composite(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  preview: boolean
): void {
  ctx.fillStyle = '#000';
  ctx.fillRect(0, 0, w, h);
  const clips = activeClips();
  if (!clips.length) return;

  const margin = Math.max(6, Math.round(w * 0.012));

  switch (state.layout) {
    case 'single': {
      drawClip(ctx, clips[0], 0, 0, w, h, preview);
      break;
    }

    case 'wipe': {
      const a = clips[0];
      const b = clips[1] ?? clips[0];
      const divider = Math.round(state.wipe * w);
      drawClip(ctx, a, 0, 0, w, h, preview);
      ctx.save();
      ctx.beginPath();
      ctx.rect(divider, 0, w - divider, h);
      ctx.clip();
      drawClip(ctx, b, 0, 0, w, h, preview);
      ctx.restore();
      if (preview) {
        ctx.fillStyle = 'rgba(255,255,255,0.85)';
        ctx.fillRect(divider - 1, 0, 2, h);
        const r = Math.max(9, Math.round(w * 0.009));
        ctx.beginPath();
        ctx.arc(divider, h / 2, r, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = '#111';
        const s = Math.round(r * 0.45);
        ctx.beginPath();
        ctx.moveTo(divider - s - 1, h / 2);
        ctx.lineTo(divider - 2, h / 2 - s);
        ctx.lineTo(divider - 2, h / 2 + s);
        ctx.moveTo(divider + s + 1, h / 2);
        ctx.lineTo(divider + 2, h / 2 - s);
        ctx.lineTo(divider + 2, h / 2 + s);
        ctx.fill();
        drawLabel(ctx, label(a, 0), margin, margin);
        drawLabel(ctx, label(b, 1), w - margin, margin, 'right');
      }
      break;
    }

    case 'side-by-side': {
      const half = Math.floor(w / 2);
      drawClip(ctx, clips[0], 0, 0, half, h, preview);
      if (clips[1]) drawClip(ctx, clips[1], half, 0, w - half, h, preview);
      if (preview) {
        drawLabel(ctx, label(clips[0], 0), margin, margin);
        if (clips[1]) drawLabel(ctx, label(clips[1], 1), half + margin, margin);
      }
      break;
    }

    case 'top-bottom': {
      const half = Math.floor(h / 2);
      drawClip(ctx, clips[0], 0, 0, w, half, preview);
      if (clips[1]) drawClip(ctx, clips[1], 0, half, w, h - half, preview);
      if (preview) {
        drawLabel(ctx, label(clips[0], 0), margin, margin);
        if (clips[1]) drawLabel(ctx, label(clips[1], 1), margin, half + margin);
      }
      break;
    }

    case 'pip': {
      const a = clips[0];
      const b = clips[1];
      drawClip(ctx, a, 0, 0, w, h, preview);
      if (b) {
        const pw = Math.round(w * 0.32);
        const ph = Math.round((pw * b.height) / Math.max(1, b.width));
        const x = state.pipCorner.includes('l') ? margin : w - pw - margin;
        const y = state.pipCorner.includes('t') ? margin : h - ph - margin;
        drawClip(ctx, b, x, y, pw, ph, preview);
        if (preview) {
          ctx.strokeStyle = 'rgba(255,255,255,0.4)';
          ctx.strokeRect(x + 0.5, y + 0.5, pw - 1, ph - 1);
        }
      }
      if (preview) drawLabel(ctx, label(a, 0), margin, margin);
      break;
    }

    case 'grid': {
      const cw = Math.floor(w / 2);
      const ch = Math.floor(h / 2);
      const cells: Array<[number, number]> =
        clips.length === 3
          ? [
              [0, 0],
              [cw, 0],
              [Math.floor(cw / 2), ch], // 3rd clip centered on the bottom row
            ]
          : [
              [0, 0],
              [cw, 0],
              [0, ch],
              [cw, ch],
            ];
      clips.slice(0, 4).forEach((clip, i) => {
        const cell = cells[i];
        if (!cell) return;
        drawClip(ctx, clip, cell[0], cell[1], cw, ch, preview);
        if (preview) drawLabel(ctx, label(clip, i), cell[0] + margin, cell[1] + margin);
      });
      break;
    }

    case 'dominant-right': {
      const mainW = Math.round(w * (2 / 3));
      drawClip(ctx, clips[0], 0, 0, mainW, h, preview);
      const rest = clips.slice(1, 4);
      const cellH = Math.floor(h / Math.max(1, rest.length));
      rest.forEach((clip, i) => {
        drawClip(ctx, clip, mainW, i * cellH, w - mainW, cellH, preview);
        if (preview) drawLabel(ctx, label(clip, i + 1), mainW + margin, i * cellH + margin);
      });
      if (preview) drawLabel(ctx, label(clips[0], 0), margin, margin);
      break;
    }

    case 'dominant-bottom': {
      const mainH = Math.round(h * (2 / 3));
      drawClip(ctx, clips[0], 0, 0, w, mainH, preview);
      const rest = clips.slice(1, 4);
      const cellW = Math.floor(w / Math.max(1, rest.length));
      rest.forEach((clip, i) => {
        drawClip(ctx, clip, i * cellW, mainH, cellW, h - mainH, preview);
        if (preview) drawLabel(ctx, label(clip, i + 1), i * cellW + margin, mainH + margin);
      });
      if (preview) drawLabel(ctx, label(clips[0], 0), margin, margin);
      break;
    }
  }
}
